import React, { useEffect, useRef, useState } from "react";
import "./home.css";
import axios from "axios";

const TELEGRAM_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;
const TELEGRAM_API = `https://api.telegram.org/bot${TELEGRAM_TOKEN}/sendMessage`;

/* ============================================================
   HOOKS
   ============================================================ */

function useReveal(threshold = 0.15) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold }
        );

        io.observe(el);
        return () => io.disconnect();
    }, [threshold]);

    return [ref, visible];
}

function useCounter(target, threshold = 0.5) {
    const ref = useRef(null);
    const [value, setValue] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    let cur = 0;
                    const step = Math.max(1, Math.round(target / 40));
                    const timer = setInterval(() => {
                        cur += step;
                        if (cur >= target) {
                            cur = target;
                            clearInterval(timer);
                        }
                        setValue(cur);
                    }, 28);
                    io.unobserve(entry.target);
                });
            },
            { threshold }
        );

        io.observe(el);
        return () => io.disconnect();
    }, [target, threshold]);

    return [ref, value];
}

/* Wraps children in a block that fades/slides in once scrolled into view. */
const Reveal = ({ as: Tag = "div", className = "", children, ...rest }) => {
    const [ref, visible] = useReveal();
    const classes = ["reveal", visible ? "in" : "", className].filter(Boolean).join(" ");
    return (
        <Tag ref={ref} className={classes} {...rest}>
            {children}
        </Tag>
    );
};

const Stat = ({ target, label }) => {
    const [revealRef, visible] = useReveal();
    const [countRef, value] = useCounter(target);
    return (
        <div ref={revealRef} className={`stat reveal${visible ? " in" : ""}`}>
            <b ref={countRef}>{value}</b>
            <span>{label}</span>
        </div>
    );
};

/* ============================================================
   DATA
   ============================================================ */

const NAV_LINKS = [
    { href: "#about", label: "О компании" },
    { href: "#capabilities", label: "Возможности" },
    { href: "#equipment", label: "Оборудование" },
    { href: "#cameras", label: "Камеры" },
    { href: "#contact", label: "Контакты" },
];

const FLOW_STEPS = [
    { num: "01", title: "Спутник", text: "Бортовой терминал принимает сигнал GPS/ГЛОНАСС и вычисляет координаты транспортного средства." },
    { num: "02", title: "Терминал на борту", text: "Teltonika FMB920 / FMB125 собирает данные: местоположение, топливо, датчики, CAN-шину." },
    { num: "03", title: "Сервер Wialon", text: "Данные передаются по GSM/GPRS на сервер с программным обеспечением Wialon Local." },
    { num: "04", title: "Ваш личный кабинет", text: "Диспетчер и руководитель получают отчёты и карту в реальном времени с любого устройства." },
];

const CAPABILITIES = [
    {
        title: "Местоположение",
        icon: "fa-solid fa-location-dot",
        items: ["Выявление несанкционированных рейсов", "Контроль разрешённых маршрутов", "Локатор без входа в систему"],
    },
    {
        title: "Расход топлива",
        icon: "fa-solid fa-gas-pump",
        items: ["Учёт заправок и сливов", "Выявление перерасхода", "Фактические нормы расхода"],
    },
    {
        title: "Пробег",
        icon: "fa-solid fa-road",
        items: ["Автооповещение о ТО", "Расчёт с заказчиком по километражу", "Исключение махинаций со спидометром"],
    },
    {
        title: "Геозоны",
        icon: "fa-solid fa-draw-polygon",
        items: ["Контроль маршрутного задания", "Создание точек интереса (POI)", "Несанкционированный въезд/выезд"],
    },
    {
        title: "Скоростной режим",
        icon: "fa-solid fa-gauge-high",
        items: ["Отслеживание параметров движения", "Безопасность грузопассажирских перевозок"],
    },
    {
        title: "Работа водителей",
        icon: "fa-solid fa-user-tie",
        items: ["Выявление простоев и нарушений графика", "Идентификация водителя по ключу", "ECO driving — качество вождения"],
    },
    {
        title: "Доп. оборудование",
        icon: "fa-solid fa-screwdriver-wrench",
        items: ["Учёт работы генераторов, кранов, компрессоров", "Удалённая блокировка запуска", "Мониторинг температуры в рефрижераторах"],
    },
    {
        title: "Отчёты и аналитика",
        icon: "fa-solid fa-chart-line",
        items: ["«Быстрый отчёт» за период", "Email-уведомления о нарушениях", "Собственные шаблоны отчётов"],
    },
];

const EQUIPMENT = [
    { img: "/logoproducts/FMB920.png", alt: "Teltonika FMB920", title: "Teltonika FMB920", text: "Устанавливается на ТС с пластиковой приборной панелью, без подключения датчика уровня топлива.", pdf: "/logoproducts/FMB920.pdf" },
    { img: "/logoproducts/FMB125.png", alt: "Teltonika FMB125", title: "Teltonika FMB125", text: "Устанавливается на любое ТС, с подключением до 5 датчиков уровня топлива.", pdf: "/logoproducts/FMB125.pdf" },
    { img: "/logoproducts/TD150.png", alt: "Эскорт TD 150", title: "Эскорт TD 150", text: "Датчик уровня топлива, подключаемый к терминалу системы мониторинга транспорта.", pdf: "/logoproducts/TD150.pdf" },
    { img: "/logoproducts/TDBLE.png", alt: "Эскорт ТD BLE", title: "Эскорт TD BLE", text: "Беспроводной датчик уровня топлива, подключаемый к терминалу системы мониторинга транспорта по Bluetooth.", pdf: "/logoproducts/TDBLE.pdf" },
];

// TODO: замените изображения на реальные фото/модели из вашего каталога
const CAMERAS = [
    {
        brand: "HOWEN",
        img: "/logocameras/2sidesd.png",
        title: "Hero-C60S0V29",
        text: "Мини-камера для салона: следит за обстановкой в кабине, легко подключается к видеорегистратору и крепится на 3М-скотч.",
    },
    {
        brand: "HOWEN",
        img: "/logocameras/ptz.png",
        title: "PTZ AHD-камера",
        text: "Поворотная AHD-камера для уличного наблюдения: обзор 360°, чёткая картинка даже почти в темноте, управляется удалённо и не боится дождя и ударов.",
    },
    {
        brand: "HOWEN",
        img: "/logocameras/2side.png",
        title: "Hero-C60S0V42",
        text: "Компактная AHD-камера с двумя объективами для транспорта: чёткая картинка днём и ночью, встроенный микрофон и лёгкий монтаж на 3М-скотч.",
    },
    {
        brand: "HIKVISION",
        img: "/logocameras/hikvision-dome.png",
        title: "Hikvision — купольная камера",
        text: "Для помещений и территорий: чёткое изображение днём и ночью, антивандальный корпус.",
    },
    {
        brand: "HIKVISION",
        img: "/logocameras/hikvision-bullet.png",
        title: "Hikvision — уличная цилиндрическая камера",
        text: "Корпус IP67, ИК-подсветка до 30 м — для периметра, стоянок и въездных групп.",
    },
    {
        brand: "HIKVISION",
        img: "/logocameras/hikvision-ptz.png",
        title: "Hikvision — поворотная PTZ-камера",
        text: "Удалённое управление углом обзора и зумом — для больших территорий и объектов под охраной.",
    },
    {
        brand: "DAHUA",
        img: "/logocameras/dahua-bullet.png",
        title: "Dahua — уличная камера",
        text: "Влаго- и пылезащищённый корпус (IP67), ИК-подсветка до 30 м — для складов, стоянок и периметра объекта.",
    },
    {
        brand: "DAHUA",
        img: "/logocameras/dahua-dome.png",
        title: "Dahua — купольная камера",
        text: "Компактный антивандальный корпус для офисов, магазинов и помещений — почти незаметна на потолке.",
    },
    {
        brand: "DAHUA",
        img: "/logocameras/dahua-ai.png",
        title: "Dahua — камера с ИИ-детекцией",
        text: "Распознаёт людей и транспорт в кадре, отправляет уведомления о тревожных событиях в реальном времени.",
    },
];


const KARAKALPAKSTAN = ["Нукус", "Мўйноқ", "Қўнғирот", "Хўжайли", "Тахтакўпир", "Чимбай", "Тахиатош", "Беруний"];
const KHOREZM = ["Ургенч", "Хива", "Хонка", "Шаббаз", "Гурлен", "Хазарасп", "Янгиарык", "Боғот"];

const ADVANTAGES = [
    { title: "Опыт в спутниковом мониторинге", text: "Работаем по проверенным стандартам, на сертифицированном оборудовании и надёжной платформе Wialon.", icon: "fa-solid fa-star" },
    {
        title: "Только проверенное оборудование",
        text: "Teltonika, Omnicomm и «Эскорт» — с реальными гарантийными обязательствами и складским наличием.",
        icon: "fa-solid fa-shield-halved",
    },
    {
        title: "Wialon Local — ПО №1 в мире",
        text: "Более 3 500 000 подключённых объектов, доля рынка СНГ — 42%, использование в 132 странах.",
        icon: "fa-solid fa-globe",
    },
    {
        title: "Локальная выездная бригада",
        text: "Монтаж, диагностика и ремонт — база в Нукусе и Ургенче.",
        icon: "fa-solid fa-truck-fast",
    },
    {
        title: "Обучение и поддержка",
        text: "Профессиональные консультации и техническая поддержка без ограничений по времени и количеству обращений.",
        icon: "fa-solid fa-headset",
    },
    { title: "Комплексный подход", text: "От постановки задачи и подбора решения до монтажа, запуска системы и последующего обслуживания клиента.", icon: "fa-solid fa-list-check" },
];
const CLIENTS = [
    { img: "/logoclients/uzxcmg.png", alt: "UzXCMG" },
    { img: "/logoclients/karakalpaksement.png", alt: "Karakalpak Cement" },
    { img: "/logoclients/uzkorgas.png", alt: "Uz Kor Gas Chemical" },
    { img: "/logoclients/sodazavod.png", alt: "Kungrad Soda Zavod" },
    { img: "/logoclients/besqala.png", alt: "BesqalaTour" },
    { img: "/logoclients/rambuilding.png", alt: "Ram Building" },
    { img: "/logoclients/electroset.png", alt: "Нукусское ЭСП" },
    { img: "/logoclients/milliybotlers.png", alt: "Milliy Botlers" },
    { img: "/logoclients/sherin.png", alt: "Sherin Kolbasa" },
    { img: "/logoclients/adliya.png", alt: "Қорақалпоғистон Республикаси Адлия вазирлиги" },
    { img: "/logoclients/chikindi.png", alt: "Chiqindi poligonlarini boshqarish direksiyasi" },
    { img: "/logoclients/panamilk.png", alt: "Panamilk" },
    { img: "/logoclients/aralagro.png", alt: "Aral Agro" },
    { img: "/logoclients/turansawda.png", alt: "Turan Sawda Orayi" },
    { img: "/logoclients/nukusevrostroy.png", alt: "Nukus Evro Stroy" },
    { img: "/logoclients/aralsea.png", alt: "Aral Sea Discovery" },
    { img: "/logoclients/mirapteka.png", alt: "Mir Apteka" },
    { img: "/logoclients/bumer.png", alt: "Cake Bumer" },
    { img: "/logoclients/artel.png", alt: "Nukus Artel" },
    { img: "/logoclients/xorezmprinental.png", alt: "Hayot Uchun" },
    { img: "/logoclients/karakalpakwindows.png", alt: "Karakalpak Windows" },
    { img: "/logoclients/103.png", alt: "Nukus Tibbiy Tez Yordam Markazi" },
    { img: "/logoclients/muratbekmebel.png", alt: "Muratbek Mebel" },
    { img: "/logoclients/xorazminvestxolding.png", alt: "Xorazm Invest Xolding" },
];

/* ============================================================
   PAGE
   ============================================================ */

const Home = ({ page }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [heroVisualRef, heroVisualVisible] = useReveal();
    const [sending, setSending] = useState(false);
    const [serviceType, setServiceType] = useState("gps");
    const closeOnMobile = () => {
        if (typeof window !== "undefined" && window.innerWidth <= 860) {
            setMenuOpen(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.target;
        const data = Object.fromEntries(new FormData(form).entries());

        const serviceLabel = data.serviceType === "cameras" ? "Камеры видеонаблюдения" : "GPS-мониторинг";
        const extraLine =
            data.serviceType === "cameras"
                ? `<b>🎥 Тип установки:</b> ${data.installType || "-"}\n`
                : `<b>🚚 Кол-во ТС:</b> ${data.vehiclesCount || "-"}\n`;

        const text =
            `<b>🆘 Новая заявка с сайта TMS</b>\n\n` +
            `<b>🏢 Компания:</b> ${data.company || "-"}\n` +
            `<b>🆔 Имя:</b> ${data.fullName || "-"}\n` +
            `<b>📲 Телефон:</b> ${data.phone || "-"}\n` +
            `<b>💌 E-mail:</b> ${data.email || "-"}\n` +
            `<b>🛠 Услуга:</b> ${serviceLabel}\n` +
            extraLine + `\n` +
            `<b>💬 Сообщение:</b> ${data.message || "-"}`;

        setSending(true);
        try {
            await axios.post(TELEGRAM_API, {
                chat_id: TELEGRAM_CHAT_ID,
                parse_mode: "HTML",
                text,
            });
            setSubmitted(true);
            form.reset();
            setServiceType("gps"); // сброс select'а, т.к. form.reset() не трогает React state
        } catch (err) {
            console.error("Telegram send failed:", err);
            alert("Не удалось отправить заявку. Попробуйте ещё раз или позвоните нам.");
        } finally {
            setSending(false);
        }
    };
    const SEO = {
        home: {
            title: "TMS — GPS мониторинг транспорта в Нукусе и Хорезме",
            h1: "GPS мониторинг транспорта",
            desc: "Установка GPS трекеров, контроль топлива и видеонаблюдение."
        },

        gps: {
            title: "GPS мониторинг транспорта | Нукус",
            h1: "GPS мониторинг транспорта",
            desc: "Профессиональная установка GPS оборудования."
        },

        gps_nukus: {
            title: "Установка GPS в Нукусе | TMS",
            h1: "Установка GPS в Нукусе",
            desc: "Монтаж Teltonika FMB920 и FMB125."
        },

        gps_khorezm: {
            title: "GPS мониторинг Хорезм",
            h1: "GPS мониторинг Хорезм",
            desc: "GPS контроль транспорта по Хорезмской области."
        },

        gps_urgench: {
            title: "Установка GPS Ургенч",
            h1: "GPS в Ургенче",
            desc: "Монтаж GPS оборудования в Ургенче."
        },

        fuel: {
            title: "Контроль расхода топлива",
            h1: "Контроль топлива",
            desc: "TD150, BLE датчики уровня топлива."
        },

        sensor: {
            title: "Датчики уровня топлива TD150",
            h1: "Датчики топлива",
            desc: "Эскорт TD150 и TD BLE."
        },

        wialon: {
            title: "Wialon Local GPS мониторинг",
            h1: "Wialon Local",
            desc: "Платформа мониторинга транспорта."
        },

        teltonika: {
            title: "Teltonika GPS оборудование",
            h1: "Teltonika",
            desc: "FMB920 и FMB125."
        },

        fmb920: {
            title: "Teltonika FMB920",
            h1: "FMB920 GPS трекер",
            desc: "Установка GPS терминала FMB920."
        },

        fmb125: {
            title: "Teltonika FMB125",
            h1: "FMB125 GPS трекер",
            desc: "GPS терминал с подключением датчиков."
        },

        escort: {
            title: "Эскорт TD150",
            h1: "Эскорт TD150",
            desc: "Датчики уровня топлива."
        },

        video: {
            title: "Видеонаблюдение Hikvision Dahua",
            h1: "Видеонаблюдение",
            desc: "Монтаж камер любой сложности."
        },

        camera_install: {
            title: "Установка камер в Нукусе",
            h1: "Установка камер",
            desc: "Монтаж камер на дома, офисы и склады."
        },

        hikvision: {
            title: "Hikvision камеры",
            h1: "Hikvision",
            desc: "IP камеры Hikvision."
        },

        dahua: {
            title: "Dahua видеонаблюдение",
            h1: "Dahua",
            desc: "Установка Dahua камер."
        },

        faceid: {
            title: "Hikvision Face ID",
            h1: "Face ID",
            desc: "Распознавание лиц и контроль доступа."
        },

        turnstile: {
            title: "Турникет Hikvision",
            h1: "Турникеты",
            desc: "Контроль доступа и Face ID."
        },

        warehouse: {
            title: "Камеры для склада",
            h1: "Складское видеонаблюдение",
            desc: "IP камеры для складов."
        },

        office: {
            title: "Камеры для офиса",
            h1: "Офисное видеонаблюдение",
            desc: "Монтаж камер в офис."
        },

        truck: {
            title: "Камеры на грузовой транспорт",
            h1: "Камеры HOWEN",
            desc: "Видеорегистраторы и камеры на транспорт."
        },

        fleet: {
            title: "Мониторинг автопарка",
            h1: "GPS для автопарков",
            desc: "Контроль техники предприятия."
        },

        logistics: {
            title: "GPS для логистических компаний",
            h1: "GPS логистика",
            desc: "Контроль маршрутов и доставки."
        },

        contacts: {
            title: "Контакты TMS",
            h1: "Связаться с нами",
            desc: "Телефон, карта и заявка."
        },

        about: {
            title: "О компании TMS",
            h1: "Track Monitoring Service",
            desc: "GPS мониторинг и видеонаблюдение."
        }
    };
    useEffect(() => {
        document.title = SEO[page].title;
    }, [page]);
    return (
        <>
            {/* ============ HEADER ============ */}
            <header>

                <div className="wrap nav">
                    <a href="/" className="logo">
                        <img src="/logo.png" alt="" />
                    </a>

                    <nav className={`nav-links${menuOpen ? " open" : ""}`}>
                        {NAV_LINKS.map((link) => (
                            <a key={link.href} href={link.href} onClick={closeOnMobile}>
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    <div className="nav-cta">
                        <a href="tel:+998930959595" className="nav-phone">
                            <i className="fa-solid fa-phone"></i>
                            +998 93 095 95 95
                        </a>
                        <a href="#contact" className="btn btn-primary btn-sm">Оставить заявку</a>
                    </div>

                    <button className="burger" aria-label="Меню" onClick={() => setMenuOpen((o) => !o)}>
                        <span></span><span></span><span></span>
                    </button>
                </div>
            </header>

            {/* ============ HERO ============ */}
            <div className="seo-hidden" aria-hidden="false">
                <h1>{SEO[page].h1}</h1>
                <h2>{SEO[page].title}</h2>
                <p>{SEO[page].desc}</p>
            </div>
            <section className="hero">
                <div className="wrap hero-grid">
                    <div className="hero-copy">
                        <span className="eyebrow">GPS/ГЛОНАСС мониторинг · Wialon</span>
                        <h1>
                            Видим каждую единицу техники <span className="hl">в любой точке мира</span>
                        </h1>
                        <p className="lead">
                            TMS (Track Monitoring Service) — компания, специализирующаяся на GPS/ГЛОНАСС
                            мониторинге транспорта и контроле расхода топлива на платформе Wialon, с покрытием
                            по всему миру — при этом основная база наших клиентов сосредоточена в Республике
                            Каракалпакстан и Хорезмской области, где мы работаем с выездом на место.
                        </p>
                        <div className="hero-ctas">
                            <a href="#contact" className="btn btn-primary">Заказать тестовый монтаж</a>
                            <a href="#capabilities" className="btn btn-ghost">Что умеет система</a>
                        </div>
                        <div className="hero-stats">
                            <Stat target={40} label="% экономии на автопарке" />
                            <Stat target={10} label="лет опыта в GPS-мониторинге" />
                            <Stat target={132} label="страны, где работает Wialon" />
                        </div>
                    </div>

                    <div ref={heroVisualRef} className={`hero-visual reveal${heroVisualVisible ? " in" : ""}`}>
                        <div className="panel-frame">
                            <div className="panel-frame-head">
                                <span className="label">TMS · Обмен данными в реальном времени 24/7</span>
                                <div className="dots"><span></span><span></span><span></span></div>
                            </div>

                            <svg
                                viewBox="0 0 560 300"
                                width="100%"
                                height="auto"
                                role="img"
                                aria-label="Схема связи: телефон клиента / ПК — сервер Wialon — транспорт, спутник над транспортом"
                            >
                                <path className="route-line" d="M92,210 L253,210" />
                                <path className="route-line" d="M307,210 L468,210" />
                                <path className="route-line" d="M280,70 L495,210" />

                                <circle className="packet" r="3.5">
                                    <animateMotion dur="1.8s" begin="0s" repeatCount="indefinite" path="M92,210 L253,210" />
                                </circle>
                                <circle className="packet packet-alt" r="3.5">
                                    <animateMotion dur="1.8s" begin=".9s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" path="M92,210 L253,210" />
                                </circle>
                                <circle className="packet" r="3.5">
                                    <animateMotion dur="1.8s" begin=".6s" repeatCount="indefinite" path="M307,210 L468,210" />
                                </circle>
                                <circle className="packet packet-alt" r="3.5">
                                    <animateMotion dur="1.8s" begin="1.5s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" path="M307,210 L468,210" />
                                </circle>
                                <circle className="packet" r="3">
                                    <animateMotion dur="1.3s" begin="1.2s" repeatCount="indefinite" path="M280,70 L495,210" />
                                </circle>
                                <circle className="packet packet-alt" r="3">
                                    <animateMotion dur="1.3s" begin="1.9s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" path="M280,70 L495,210" />
                                </circle>

                                <g transform="translate(65,210)">
                                    <circle className="badge-pulse" r="27" />
                                    <circle className="node-badge" r="27" />
                                    <rect x="-15" y="-11" width="20" height="14" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                    <line x1="-5" y1="3" x2="-5" y2="7" stroke="var(--accent)" strokeWidth="1.6" />
                                    <line x1="-9" y1="7" x2="-1" y2="7" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" />
                                    <rect x="3" y="-2" width="9" height="15" rx="2" fill="var(--panel)" stroke="var(--accent)" strokeWidth="1.6" />
                                    <line x1="6" y1="10" x2="9" y2="10" stroke="var(--accent)" strokeWidth="1.4" strokeLinecap="round" />
                                </g>
                                <text x="65" y="264" textAnchor="middle" className="node-label hq">ТЕЛЕФОН КЛИЕНТА / ПК</text>

                                <g transform="translate(280,210)">
                                    <circle className="badge-pulse" r="27" style={{ animationDelay: ".5s" }} />
                                    <circle className="node-badge" r="27" />
                                    <rect x="-13" y="-12" width="26" height="10" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                    <rect x="-13" y="1" width="26" height="10" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                    <circle cx="-7" cy="-7" r="1.2" fill="var(--accent)" />
                                    <circle cx="-7" cy="6" r="1.2" fill="var(--accent)" />
                                </g>
                                <text x="280" y="264" textAnchor="middle" className="node-label hq">СЕРВЕР WIALON</text>

                                <g transform="translate(495,210)">
                                    <circle className="badge-pulse" r="27" style={{ animationDelay: "1s" }} />
                                    <circle className="node-badge" r="27" />
                                    <rect x="-16" y="-4" width="19" height="11" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                    <path d="M3,-4 h8 l6,6 v5 h-14 z" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinejoin="round" />
                                    <circle cx="-9" cy="9" r="2.6" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                    <circle cx="9" cy="9" r="2.6" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                </g>
                                <text x="495" y="264" textAnchor="middle" className="node-label hq">ТРАНСПОРТ</text>

                                <g transform="translate(280,45)">
                                    <circle className="badge-pulse" r="27" style={{ animationDelay: "1.5s" }} />
                                    <circle className="node-badge" r="27" />
                                    <rect x="-6" y="-6" width="12" height="12" rx="1.5" fill="none" stroke="var(--accent)" strokeWidth="1.8" />
                                    <rect x="-19" y="-3" width="9" height="6" rx="1" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                    <rect x="10" y="-3" width="9" height="6" rx="1" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
                                    <line x1="-6" y1="0" x2="-10" y2="0" stroke="var(--accent)" strokeWidth="1.6" />
                                    <line x1="6" y1="0" x2="10" y2="0" stroke="var(--accent)" strokeWidth="1.6" />
                                    <line x1="0" y1="-6" x2="0" y2="-12" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" />
                                    <circle cx="0" cy="-13" r="1.3" fill="var(--accent)" />
                                </g>
                                <text x="240" y="41" textAnchor="end" className="node-label hq">GPS / ГЛОНАСС</text>
                                <text x="240" y="55" textAnchor="end" className="node-label">СПУТНИК</text>
                            </svg>

                            {/* <p className="map-caption">
                                телефон клиента / ПК → сервер Wialon → транспорт → спутник · данные вживую
                            </p> */}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ PARTNER STRIP ============ */}
            <div className="partner-strip">
                <div className="wrap">
                    <span className="tag">Работаем на оборудовании и ПО</span>
                    <div className="items">
                        <b>Wialon Local</b>
                        <b>Teltonika</b>
                        <b>Omnicomm</b>
                        <b>Эскорт</b>
                    </div>
                </div>
            </div>

            {/* ============ ABOUT ============ */}
            <section id="about">
                <div className="wrap">
                    <div className="about-grid">
                        <Reveal>
                            <span className="eyebrow">О компании</span>
                            <h2 style={{ marginTop: "14px" }}>TMS — локальная экспертиза и проверенные технологии</h2>
                            <p style={{ marginTop: "20px" }}>
                                TMS (Track Monitoring Service) — компания, специализирующаяся на спутниковом
                                мониторинге транспорта. Платформа Wialon, на которой мы работаем, охватывает 132
                                страны, поэтому транспорт клиента можно контролировать в любой точке мира, где
                                бы он ни находился.
                            </p>
                            <p>
                                При этом наша основная клиентская база сосредоточена в Республике Каракалпакстан
                                и Хорезмской области — именно здесь у нас выездная бригада, склад оборудования и
                                локальная поддержка. Мы берём на себя всё, что происходит на месте: выезд
                                специалиста, монтаж оборудования, настройку отчётов и сопровождение клиента — на
                                проверенной платформе <strong>Wialon Local</strong> и сертифицированном
                                оборудовании Teltonika, Omnicomm и «Эскорт».
                            </p>
                            <p>
                                Такой формат даёт клиентам две вещи одновременно: глобальное покрытие
                                платформы мирового уровня — и скорость локальной команды именно в вашем
                                регионе.
                            </p>
                        </Reveal>

                        <Reveal>
                            <span className="eyebrow">Как это работает</span>
                            <div className="flow">
                                {FLOW_STEPS.map((step) => (
                                    <div className="flow-step" key={step.num}>
                                        <span className="flow-num">{step.num}</span>
                                        <div>
                                            <h4>{step.title}</h4>
                                            <p>{step.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ CAPABILITIES ============ */}
            <section id="capabilities" className="section-alt">
                <div className="wrap">
                    <Reveal className="section-head">
                        <span className="eyebrow">Возможности системы</span>
                        <h2>Полный контроль над автопарком — по каждому параметру</h2>
                        <p>
                            Готовые решения для оптимизации затрат и повышения эффективности использования
                            транспорта, построенные на платформе Wialon.
                        </p>
                    </Reveal>

                    <div className="cap-grid">
                        {CAPABILITIES.map((cap) => (
                            <Reveal className="cap-card" key={cap.title}>
                                <div className="icon">
                                    <i className={cap.icon}></i>
                                </div>
                                <h4>{cap.title}</h4>
                                <ul>
                                    {cap.items.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ EQUIPMENT ============ */}
            <section id="equipment">
                <div className="wrap">
                    <Reveal className="section-head">
                        <span className="eyebrow">Оборудование</span>
                        <h2>Сертифицированные GPS-трекеры и датчики топлива</h2>
                        <p>
                            Поставляем и устанавливаем сертифицированное оборудование — с гарантией и
                            сервисной поддержкой на месте.
                        </p>
                    </Reveal>

                    <div className="eq-grid">
                        {EQUIPMENT.map((item) => (
                            <Reveal className="eq-card" key={item.title}>
                                <div className="thumb">
                                    <img src={item.img} alt={item.alt} loading="lazy" />
                                </div>
                                <h4>{item.title}</h4>
                                <p>{item.text}</p>
                                <a className="flyer" href={item.pdf} target="_blank" rel="noopener noreferrer">
                                    Флаер PDF <i className="fa-solid fa-arrow-up-right-from-square"></i>
                                </a>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="eq-note">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>
                            У нас можно найти оборудование практически любого типа и производителя —
                            включая китайские марки, такие как <strong>Sinotruck</strong> и другие.
                            Если вам нужна техника под конкретную марку — уточните у менеджера TMS,
                            подберём подходящее решение.
                        </span>
                    </Reveal>
                </div>
            </section>

            {/* ============ CAMERAS ============ */}
            <section id="cameras" >
                <div className="wrap">
                    <Reveal className="section-head">
                        <span className="eyebrow">Видеонаблюдение</span>
                        <h2>Камеры на транспорт и любые объекты</h2>
                        <p>
                            Кроме GPS-мониторинга устанавливаем видеорегистраторы <strong>HOWEN</strong> для
                            транспорта и IP-камеры <strong>Hikvision</strong> и <strong>Dahua</strong> — на
                            автомобили, склады, офисы и любые другие объекты.
                        </p>
                    </Reveal>

                    <div className="cam-grid">
                        {CAMERAS.map((cam) => (
                            <Reveal className="eq-card cam-card" key={cam.title}>
                                <span className={`cam-badge cam-badge-${cam.brand.toLowerCase()}`}>{cam.brand}</span>
                                <div className="thumb">
                                    <img src={cam.img} alt={cam.title} loading="lazy" />
                                </div>
                                <h4>{cam.title}</h4>
                                <p>{cam.text}</p>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="eq-note">
                        <i className="fa-solid fa-video"></i>
                        <span>
                            Устанавливаем камеры <strong>Hikvision</strong> и <strong>Dahua</strong> не только
                            на здания и территории, но и на сам транспорт — в паре с GPS-мониторингом это даёт
                            полную картину: где находится техника и что происходит вокруг неё.
                        </span>
                    </Reveal>
                </div>
            </section>

            {/* ============ REGION ============ */}
            <section id="region" className="section-alt">
                <div className="wrap region-grid">
                    <Reveal>
                        <span className="eyebrow">Покрытие и клиентская база</span>
                        <h2 style={{ marginTop: "14px" }}>Мониторинг — по всему миру, сервис — рядом с вами</h2>
                        <p style={{ color: "var(--text-muted)", marginTop: "16px" }}>
                            Платформа Wialon позволяет отслеживать транспорт в любой стране. Но основная часть
                            наших клиентов работает в Республике Каракалпакстан и Хорезмской области — здесь
                            базируется выездная бригада TMS, которая оперативно закрывает монтаж, диагностику и
                            ремонт оборудования на месте.
                        </p>
                        {/* <div className="region-list">
                            <p className="region-name">Основная база · Каракалпакстан</p>
                            {KARAKALPAKSTAN.map((city) => (
                                <li key={city}>{city}</li>
                            ))}
                            <p className="region-name">Основная база · Хорезмская область</p>
                            {KHOREZM.map((city) => (
                                <li key={city}>{city}</li>
                            ))}
                        </div> */}
                    </Reveal>

                    <Reveal className="map-panel">


                        <svg
                            viewBox="0 0 560 300"
                            width="100%"
                            height="auto"
                            role="img"
                            aria-label="Глобус: покрытие Wialon по всему миру, основная клиентская база TMS — Каракалпакстан и Хорезм"
                        >
                            <defs>
                                <radialGradient id="globeGrad" cx="34%" cy="28%" r="78%">
                                    <stop offset="0%" stopColor="var(--panel)" />
                                    <stop offset="60%" stopColor="var(--panel-2)" />
                                    <stop offset="100%" stopColor="var(--bg-alt)" />
                                </radialGradient>
                                <radialGradient id="globeShade" cx="68%" cy="75%" r="60%">
                                    <stop offset="0%" stopColor="var(--accent)" stopOpacity=".07" />
                                    <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
                                </radialGradient>
                            </defs>

                            <ellipse cx="280" cy="266" rx="78" ry="10" fill="var(--line)" opacity=".45" />

                            <circle cx="280" cy="148" r="134" className="route-line" fill="none" />
                            <circle r="3.4" fill="var(--accent)" filter="drop-shadow(0 0 4px var(--accent-soft))">
                                <animateMotion dur="16s" repeatCount="indefinite" path="M280,14 A134,134 0 0,1 280,282 A134,134 0 0,1 280,14 Z" />
                            </circle>

                            <circle cx="280" cy="148" r="104" fill="url(#globeGrad)" stroke="var(--line)" strokeWidth="1.4" />
                            <circle cx="280" cy="148" r="104" fill="url(#globeShade)" />
                            <clipPath id="globeClip">
                                <circle cx="280" cy="148" r="104" />
                            </clipPath>

                            <g clipPath="url(#globeClip)">
                                <ellipse cx="280" cy="148" rx="104" ry="22" className="globe-lat" />
                                <ellipse cx="280" cy="107" rx="98" ry="14" className="globe-lat" />
                                <ellipse cx="280" cy="189" rx="98" ry="14" className="globe-lat" />
                                <ellipse cx="280" cy="72" rx="78" ry="7" className="globe-lat" />
                                <ellipse cx="280" cy="224" rx="78" ry="7" className="globe-lat" />
                                <ellipse cx="280" cy="148" rx="78" ry="104" className="globe-meridian-static" />
                                <ellipse cx="280" cy="148" rx="40" ry="104" className="globe-meridian-static" />
                                <line x1="280" y1="44" x2="280" y2="252" stroke="var(--line)" strokeWidth="1" opacity=".7" />
                                <ellipse cx="280" cy="148" rx="2" ry="104" className="globe-meridian-spin" />

                                <circle cx="245" cy="185" r="2.2" className="globe-dot-dim" />
                                <circle cx="325" cy="95" r="2.2" className="globe-dot-dim" />
                                <circle cx="345" cy="160" r="2.2" className="globe-dot-dim" />
                                <circle cx="308" cy="205" r="2.2" className="globe-dot-dim" />
                                <circle cx="235" cy="125" r="2.2" className="globe-dot-dim" />
                                <circle cx="300" cy="110" r="2.2" className="globe-dot-dim" />
                                <circle cx="260" cy="90" r="2.2" className="globe-dot-dim" />
                            </g>
                            <circle cx="280" cy="148" r="104" fill="none" stroke="var(--line)" strokeWidth="1.4" />

                            <circle cx="280" cy="44" r="2" fill="var(--text-dim)" />
                            <circle cx="280" cy="252" r="2" fill="var(--text-dim)" />

                            <circle cx="220" cy="112" r="4.5" className="globe-marker" />
                            <circle cx="220" cy="112" r="5" className="globe-marker-ring" />

                            <text x="18" y="48" className="node-label" style={{ fontSize: "10px", letterSpacing: ".08em" }}>
                            </text>
                            <text x="18" y="62" className="node-label hq" style={{ fontSize: "11.5px" }}>
                            </text>

                            <text x="502" y="286" textAnchor="end" className="node-label" style={{ fontSize: "10px", letterSpacing: ".06em" }}>
                                WIALON · 132 СТРАНЫ
                            </text>
                        </svg>
                    </Reveal>
                </div>
            </section>

            {/* ============ ADVANTAGES ============ */}
            <section id="advantages">
                <div className="wrap">
                    <Reveal className="section-head">
                        <span className="eyebrow">Почему TMS</span>
                        <h2>Проверенные технологии плюс присутствие в регионе</h2>
                    </Reveal>

                    <div className="adv-grid">
                        {ADVANTAGES.map((adv) => (
                            <Reveal className="adv-card" key={adv.title}>
                                <div className="icon">
                                    <i className={adv.icon}></i>
                                </div>
                                <h4>{adv.title}</h4>
                                <p>{adv.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
            {/* ============ CLIENTS ============ */}
            <section id="clients">
                <div className="wrap">
                    <Reveal className="section-head">
                        <span className="eyebrow">Нам доверяют</span>
                        <h2>Клиенты сети TMS по Узбекистану</h2>
                    </Reveal>

                    <Reveal as="p" className="clients-note" >
                        Wialon и оборудование, которое устанавливает TMS в вашем регионе, уже работает в
                        автопарках этих компаний по всей стране.
                    </Reveal>

                    <div className="clients-grid">
                        {CLIENTS.map((client) => (
                            <Reveal className="client-cell" key={client.alt}>
                                <img src={client.img} title={client.alt} loading="lazy" />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
            {/* ============ CONTACT ============ */}
            <section id="contact" className="section-alt">
                <div className="wrap">
                    <Reveal className="section-head">
                        <span className="eyebrow">Связаться с TMS</span>
                        <h2>Запросите тестовую установку для вашего автопарка</h2>
                        <p>При автопарке от 20 единиц — бесплатное оснащение нескольких транспортных средств тестовым образцом.</p>
                    </Reveal>

                    <div className="contact-grid">
                        <Reveal className="form-panel">
                            {!submitted ? (
                                <form onSubmit={handleSubmit}>
                                    <div className="form-row">
                                        <div className="field">
                                            <label>Название компании</label>
                                            <input type="text" name="company" placeholder="ООО «Пример»" />
                                        </div>
                                        <div className="field">
                                            <label>Ваше имя*</label>
                                            <input type="text" name="fullName" placeholder="Имя Фамилия" required />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="field">
                                            <label>Телефон*</label>
                                            <input type="tel" name="phone" placeholder="+998 __ ___-__-__" required />
                                        </div>
                                        <div className="field">
                                            <label>E-mail</label>
                                            <input type="email" name="email" placeholder="mail@company.uz" />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="field">
                                            <label>Тип услуги</label>
                                            <select
                                                name="serviceType"
                                                value={serviceType}
                                                onChange={(e) => setServiceType(e.target.value)}
                                            >
                                                <option value="gps">GPS-мониторинг</option>
                                                <option value="cameras">Камеры видеонаблюдения</option>
                                            </select>
                                        </div>

                                        {serviceType === "gps" ? (
                                            <div className="field">
                                                <label>Количество ТС</label>
                                                <input type="number" name="vehiclesCount" placeholder="напр. 12" min="1" />
                                            </div>
                                        ) : (
                                            <div className="field">
                                                <label>Тип установки</label>
                                                <select name="installType" defaultValue="Автомобиль">
                                                    <option>Автомобиль</option>
                                                    <option>Помещение / объект</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                    <div className="field full" style={{ marginBottom: "16px" }}>
                                        <label>Сообщение</label>
                                        <textarea name="message" placeholder="Расскажите о вашем автопарке (помещении) и задачах" />
                                    </div>
                                    <div className="form-foot">
                                        <p>Отправляя форму, вы соглашаетесь на обработку контактных данных менеджером TMS.</p>
                                        <button type="submit" className="btn btn-primary" disabled={sending}>
                                            {sending ? "Отправка..." : "Отправить заявку"}
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="form-success show">
                                    <i className="fa-solid fa-circle-check"></i>
                                    <h4>Заявка принята</h4>
                                    <p>Менеджер TMS свяжется с вами в ближайшее рабочее время.</p>
                                </div>
                            )}
                        </Reveal>

                        <div className="contact-side">
                            <Reveal className="info-card">
                                <div className="icon">
                                    <i className="fa-solid fa-envelope"></i>
                                </div>
                                <div>
                                    <h4>E-mail</h4>
                                    <a href="mailto:trackmonitoringservice@gmail.com">trackmonitoringservice@gmail.com</a>
                                </div>
                            </Reveal>

                            <Reveal className="info-card">
                                <div className="icon">
                                    <i className="fa-solid fa-phone"></i>
                                </div>
                                <div>
                                    <h4>Телефон / диспетчер</h4>
                                    <a href="tel:+998930959595">+998 93 095 95 95</a>
                                </div>
                            </Reveal>

                            <Reveal className="info-card">
                                <div className="icon">
                                    <i className="fa-solid fa-location-dot"></i>
                                </div>
                                <div>
                                    <h4>Офисы</h4>
                                    <p>
                                        г. Нукус, Республика Каракалпакстан
                                    </p>
                                </div>
                            </Reveal>
                            <Reveal className="info-card maps-card">
                                <iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2944.084562865679!2d59.620217798071266!3d42.4472168388059!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x41dd9babc7f803e3%3A0xde26392081a03776!2sTrack%20Monitoring%20Service!5e0!3m2!1sru!2s!4v1785841250814!5m2!1sru!2s" allowfullscreen loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>
                            </Reveal>
                            <Reveal className="info-card">
                                <div className="icon">
                                    <i className="fa-solid fa-clock"></i>
                                </div>
                                <div>
                                    <h4>Режим работы</h4>
                                    <p>Пн–Пт, 09:00–18:00</p>
                                    <p>Сб, 09:00–12:30</p>
                                    <p>Обеденное время, 12:30–14:30</p>
                                    <p>Вс, <span style={{ color: "red" }}>Выходной</span></p>
                                </div>
                            </Reveal>

                            <Reveal className="social-row">
                                <a href="https://www.instagram.com/gps_nukus" aria-label="Instagram">
                                    <i className="fa-brands fa-instagram"></i>
                                </a>
                                <a href="https://t.me/+998930959595" aria-label="Telegram">
                                    <i className="fa-brands fa-telegram"></i>
                                </a>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ FOOTER ============ */}
            <footer>
                <div className="wrap">
                    <div className="footer-grid">
                        <div className="footer-col footer-about">
                            <a href="/" className="logo">
                                <img src="/logo.png" alt="" />
                            </a>
                            <p>
                                GPS-мониторинг транспорта и контроль расхода топлива с покрытием по всему миру
                                — основная клиентская база в Каракалпакстане и Хорезмской области.
                            </p>
                        </div>

                        <div className="footer-col">
                            <h4>Компания</h4>
                            <ul>
                                <li><a href="#about">О компании</a></li>
                                <li><a href="#advantages">Почему TMS</a></li>
                                <li><a href="#contact">Контакты</a></li>
                            </ul>
                        </div>

                        <div className="footer-col">
                            <h4>Продукт</h4>
                            <ul>
                                <li><a href="#capabilities">Возможности</a></li>
                                <li><a href="#equipment">Оборудование</a></li>
                                <li><a href="#cameras">Камеры</a></li>
                                <li><a href="#region">Зона покрытия</a></li>
                            </ul>
                        </div>

                        <div className="footer-col">
                            <h4>Контакты</h4>
                            <ul>
                                <li><a href="mailto:trackmonitoringservice@gmail.com">trackmonitoringservice@gmail.com</a></li>
                                <li><a href="tel:+998930959595">+998 93 095 95 95</a></li>
                                <li><a href="#contact">Оставить заявку</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="footer-bottom">
                        <p>© 2016 TMS — OOO Track Monitoring Service</p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Home;